import {
  AlertCircle,
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface DateRangePickerProps {
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Hint text shown below input */
  hint?: string;
  /** Currently selected date range */
  value?: DateRange;
  /** Callback when date range changes */
  onChange?: (range: DateRange) => void;
  /** Placeholder for start date */
  startPlaceholder?: string;
  /** Placeholder for end date */
  endPlaceholder?: string;
  /** Minimum selectable date */
  minDate?: Date | string;
  /** Maximum selectable date */
  maxDate?: Date | string;
  /** Date format for display */
  dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  /** Disable the input */
  disabled?: boolean;
  /** Required field */
  required?: boolean;
  /** Show clear button */
  showClear?: boolean;
  /** Show preset ranges (Last 7 days, Last 30 days, etc.) */
  showPresets?: boolean;
  /** First day of week (0 = Sunday, 1 = Monday) */
  firstDayOfWeek?: 0 | 1;
  /** Custom class name */
  className?: string;
  /** Input ID */
  id?: string;
  /** Compact mode for toolbar filters */
  compact?: boolean;
}

export interface DateRangePickerRef {
  focus: () => void;
  blur: () => void;
  clear: () => void;
}

const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const PRESET_RANGES = [
  { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: 'Last 7 days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last 30 days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'This month',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last month',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'This year',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      return { startDate: start, endDate: end };
    },
  },
];

const DateRangePicker = forwardRef<DateRangePickerRef, DateRangePickerProps>(
  (
    {
      label,
      error,
      hint,
      value,
      onChange,
      startPlaceholder: _startPlaceholder = 'Start date',
      endPlaceholder: _endPlaceholder = 'End date',
      minDate,
      maxDate,
      dateFormat = 'MM/DD/YYYY',
      disabled = false,
      required = false,
      showClear = true,
      showPresets = true,
      firstDayOfWeek = 0,
      className = '',
      id,
      compact = false,
    },
    ref
  ) => {
    const inputId = id || `daterangepicker-${Math.random().toString(36).substr(2, 9)}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // State
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date());
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [selectingEnd, setSelectingEnd] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Calculate dropdown position
    useEffect(() => {
      if (isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = 420;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top = rect.bottom + 4;
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          top = rect.top - dropdownHeight - 4;
        }

        setDropdownPosition({
          top: Math.max(8, top),
          left: rect.left,
        });
      }
    }, [isOpen]);

    // Parse value prop
    useEffect(() => {
      if (value) {
        if (value.startDate) {
          const date = new Date(value.startDate);
          if (!isNaN(date.getTime())) {
            setStartDate(date);
            setViewDate(date);
          }
        } else {
          setStartDate(null);
        }

        if (value.endDate) {
          const date = new Date(value.endDate);
          if (!isNaN(date.getTime())) {
            setEndDate(date);
          }
        } else {
          setEndDate(null);
        }
      } else {
        setStartDate(null);
        setEndDate(null);
      }
    }, [value]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      clear: () => handleClear(),
    }));

    // Handle click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if date is disabled
    const isDateDisabled = useCallback(
      (date: Date) => {
        const parsedMin = minDate
          ? typeof minDate === 'string'
            ? new Date(minDate)
            : minDate
          : null;
        const parsedMax = maxDate
          ? typeof maxDate === 'string'
            ? new Date(maxDate)
            : maxDate
          : null;
        if (parsedMin && date < parsedMin) return true;
        if (parsedMax && date > parsedMax) return true;
        return false;
      },
      [minDate, maxDate]
    );

    // Format date for display
    const _formatDate = useCallback(
      (date: Date | null): string => {
        if (!date) return '';

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        switch (dateFormat) {
          case 'DD/MM/YYYY':
            return `${day}/${month}/${year}`;
          case 'YYYY-MM-DD':
            return `${year}-${month}-${day}`;
          default:
            return `${month}/${day}/${year}`;
        }
      },
      [dateFormat]
    );

    // Format short date for compact mode
    const formatShortDate = useCallback((date: Date | null): string => {
      if (!date) return '';
      const day = date.getDate();
      const month = MONTHS_SHORT[date.getMonth()];
      return `${month} ${day}`;
    }, []);

    // Get calendar days
    const getCalendarDays = useCallback(
      (monthOffset: number = 0) => {
        const year =
          viewDate.getMonth() + monthOffset > 11
            ? viewDate.getFullYear() + 1
            : viewDate.getMonth() + monthOffset < 0
              ? viewDate.getFullYear() - 1
              : viewDate.getFullYear();
        const month = (viewDate.getMonth() + monthOffset + 12) % 12;

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const startOffset = (firstDay.getDay() - firstDayOfWeek + 7) % 7;
        const days: (Date | null)[] = [];

        // Add empty cells for days before first day of month
        for (let i = 0; i < startOffset; i++) {
          const prevDate = new Date(year, month, -startOffset + i + 1);
          days.push(prevDate);
        }

        // Add days of the month
        for (let i = 1; i <= lastDay.getDate(); i++) {
          days.push(new Date(year, month, i));
        }

        // Add empty cells for days after last day of month to complete 6 rows
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
          const nextDate = new Date(year, month + 1, i);
          days.push(nextDate);
        }

        return days;
      },
      [viewDate, firstDayOfWeek]
    );

    // Check if date is today
    const isToday = (date: Date) => {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    };

    // Check if date is selected (start or end)
    const isSelected = (date: Date) => {
      return isStartDate(date) || isEndDate(date);
    };

    // Check if date is start date
    const isStartDate = (date: Date) => {
      if (!startDate) return false;
      return (
        date.getDate() === startDate.getDate() &&
        date.getMonth() === startDate.getMonth() &&
        date.getFullYear() === startDate.getFullYear()
      );
    };

    // Check if date is end date
    const isEndDate = (date: Date) => {
      if (!endDate) return false;
      return (
        date.getDate() === endDate.getDate() &&
        date.getMonth() === endDate.getMonth() &&
        date.getFullYear() === endDate.getFullYear()
      );
    };

    // Check if date is in range
    const isInRange = (date: Date) => {
      if (!startDate) return false;

      const effectiveEndDate = endDate || hoverDate;
      if (!effectiveEndDate) return false;

      const start = startDate.getTime();
      const end = effectiveEndDate.getTime();
      const current = date.getTime();

      return current > Math.min(start, end) && current < Math.max(start, end);
    };

    // Check if date is in current month
    const isCurrentMonth = (date: Date, monthOffset: number = 0) => {
      const targetMonth = (viewDate.getMonth() + monthOffset + 12) % 12;
      return date.getMonth() === targetMonth;
    };

    // Handle date selection
    const handleSelectDate = (date: Date) => {
      if (isDateDisabled(date)) return;

      if (!selectingEnd || !startDate) {
        // Selecting start date
        setStartDate(date);
        setEndDate(null);
        setSelectingEnd(true);
      } else {
        // Selecting end date
        let newStart = startDate;
        let newEnd = date;

        if (date < startDate) {
          // If end date is before start, swap them
          newStart = date;
          newEnd = startDate;
        }

        setStartDate(newStart);
        setEndDate(newEnd);
        setSelectingEnd(false);

        // Emit change
        onChange?.({
          startDate: newStart.toISOString().split('T')[0],
          endDate: newEnd.toISOString().split('T')[0],
        });

        setIsOpen(false);
      }
    };

    // Handle preset selection
    const handlePresetSelect = (preset: (typeof PRESET_RANGES)[0]) => {
      const { startDate: start, endDate: end } = preset.getValue();
      setStartDate(start);
      setEndDate(end);
      setViewDate(start);

      onChange?.({
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      });

      setIsOpen(false);
    };

    // Navigation handlers
    const handlePrevMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // Handle clear
    const handleClear = () => {
      setStartDate(null);
      setEndDate(null);
      setSelectingEnd(false);
      onChange?.({ startDate: null, endDate: null });
    };

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
      }
    };

    // Adjust days display for first day of week
    const displayDays = firstDayOfWeek === 1 ? [...DAYS_SHORT.slice(1), DAYS_SHORT[0]] : DAYS_SHORT;

    // Get month/year for display
    const getMonthYear = (monthOffset: number = 0) => {
      const month = (viewDate.getMonth() + monthOffset + 12) % 12;
      const year =
        viewDate.getMonth() + monthOffset > 11
          ? viewDate.getFullYear() + 1
          : viewDate.getMonth() + monthOffset < 0
            ? viewDate.getFullYear() - 1
            : viewDate.getFullYear();
      return `${MONTHS[month]} ${year}`;
    };

    // Render calendar
    const renderCalendar = (monthOffset: number = 0) => (
      <div className="p-4" style={{ width: '280px' }}>
        {/* Month Header */}
        <div className="text-center mb-3">
          <span className="text-sm font-semibold text-gray-800">{getMonthYear(monthOffset)}</span>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 mb-2">
          {displayDays.map((day, index) => (
            <div
              key={index}
              className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7">
          {getCalendarDays(monthOffset).map((date, index) => {
            if (!date) return <div key={index} className="h-9" />;

            const dateDisabled = isDateDisabled(date);
            const isStart = isStartDate(date);
            const isEnd = isEndDate(date);
            const inRange = isInRange(date);
            const today = isToday(date);
            const currentMonth = isCurrentMonth(date, monthOffset);

            // Determine background/text styles
            let cellClasses =
              'h-9 w-full flex items-center justify-center text-sm transition-colors ';

            if (dateDisabled) {
              cellClasses += 'text-gray-300 cursor-not-allowed ';
            } else if (isStart || isEnd) {
              cellClasses += 'bg-[#E3222A] text-white font-medium cursor-pointer ';
              if (isStart && (endDate || hoverDate)) {
                cellClasses += 'rounded-l-full ';
              } else if (isEnd && startDate) {
                cellClasses += 'rounded-r-full ';
              } else {
                cellClasses += 'rounded-full ';
              }
            } else if (inRange) {
              cellClasses += 'bg-red-50 text-gray-800 cursor-pointer ';
            } else if (today) {
              cellClasses +=
                'border-2 border-[#E3222A] text-[#E3222A] font-medium rounded-full cursor-pointer ';
            } else if (currentMonth) {
              cellClasses += 'text-gray-700 hover:bg-gray-100 rounded-full cursor-pointer ';
            } else {
              cellClasses += 'text-gray-400 hover:bg-gray-50 rounded-full cursor-pointer ';
            }

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectDate(date)}
                onMouseEnter={() => selectingEnd && setHoverDate(date)}
                onMouseLeave={() => setHoverDate(null)}
                disabled={dateDisabled}
                className={cellClasses}
                aria-label={date.toLocaleDateString()}
                aria-selected={isSelected(date)}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );

    // Dropdown content
    const dropdownContent = isOpen && (
      <div
        ref={dropdownRef}
        className="fixed bg-white rounded-xl shadow-2xl border border-gray-200"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          zIndex: 99999,
        }}
        role="dialog"
        aria-label="Date range picker"
      >
        <div className="flex">
          {/* Presets */}
          {showPresets && (
            <div className="w-32 border-r border-gray-100 py-3 bg-gray-50 rounded-l-xl">
              <div className="px-3 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Quick Select
              </div>
              {PRESET_RANGES.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-white hover:text-[#E3222A] transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          {/* Calendars */}
          <div className="flex flex-col">
            {/* Navigation Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>

              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Next month"
              >
                <ChevronRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Dual Calendar */}
            <div className="flex divide-x divide-gray-100">
              {renderCalendar(0)}
              {renderCalendar(1)}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <div className="text-sm text-gray-600">
                {selectingEnd && startDate
                  ? 'Select end date'
                  : startDate && endDate
                    ? `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`
                    : 'Select start date'}
              </div>
              <div className="flex items-center gap-2">
                {showClear && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm bg-[#E3222A] text-white rounded-lg hover:bg-[#C7141B] transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div className={`font-['Inter',sans-serif] ${className}`} ref={containerRef}>
        {/* Label */}
        {label && !compact && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1.5">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input */}
        <div className="relative">
          <div
            ref={inputRef}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            className={`
              inline-flex items-center rounded-lg border bg-white transition-all duration-200 cursor-pointer
              ${compact ? 'h-9 px-3' : 'h-10 px-3'}
              ${isOpen ? 'border-[#E3222A] ring-2 ring-[#E3222A] ring-opacity-20' : 'border-gray-300'}
              ${error ? 'border-red-300' : ''}
              ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'hover:border-[#E3222A]'}
            `}
            role="button"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
          >
            <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />

            <div className="flex items-center text-sm whitespace-nowrap">
              <span className={startDate ? 'text-gray-900' : 'text-gray-500'}>
                {startDate ? formatShortDate(startDate) : 'Start'}
              </span>
              <ArrowRight className="h-3.5 w-3.5 mx-2 text-gray-400" />
              <span className={endDate ? 'text-gray-900' : 'text-gray-500'}>
                {endDate ? formatShortDate(endDate) : 'End'}
              </span>
            </div>

            {/* Clear button */}
            {showClear && (startDate || endDate) && !disabled && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="ml-2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear date range"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <ChevronDown
              className={`h-4 w-4 text-gray-400 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Dropdown via Portal */}
        {createPortal(dropdownContent, document.body)}

        {/* Error Message */}
        {error && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle size={14} className="flex-shrink-0" />
            {error}
          </p>
        )}

        {/* Hint Text */}
        {hint && !error && <p className="mt-1.5 text-sm text-gray-500">{hint}</p>}
      </div>
    );
  }
);

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
